// src/context/AppContext.jsx - Relationship Compass Global App Context
//
// Local first: assessment results, module progress, tool data and tool
// usage are saved on this device every time, signed in or not. With cloud
// backup switched on, services/hubSync.js copies them (and every tool's own
// entries) to the person's DDD account in the Auth Hub.
//
// Until October 2026 these saves went through storageService with the
// preference 'firebase', which matched none of its branches, so nothing was
// saved anywhere and results and progress were lost on reload.

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  saveAssessmentResults,
  saveModuleProgress,
  saveToolData,
  saveToolUsage,
  loadAllUserData,
  migrateOldLocalStorage
} from '../services/storageService';
import { useGamificationStore } from '../stores/gamificationStore';
import * as hubSync from '../services/hubSync';

const AppContext = createContext({});

// Everything AppContext keeps lives on this device under these names.
const DEVICE_ONLY = 'local';

// eslint-disable-next-line react-refresh/only-export-components -- hook shares the provider's context
export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [assessmentResults, setAssessmentResultsState] = useState(null);
  const [moduleProgress, setModuleProgressState] = useState({});
  const [toolsUsage, setToolsUsageState] = useState({});
  const [toolData, setToolDataState] = useState({});
  const [theme, setTheme] = useState('dark');
  const [notifications, setNotifications] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [cloudSyncEnabled, setCloudSyncEnabled] = useState(hubSync.isBackupOn);

  // Read what this device holds into state.
  const loadFromDevice = useCallback(async () => {
    try {
      migrateOldLocalStorage();
      const data = await loadAllUserData(null, DEVICE_ONLY);
      setAssessmentResultsState(data.assessmentResults || null);
      setModuleProgressState(data.moduleProgress || {});
      setToolDataState(data.toolData || {});
      setToolsUsageState(data.toolUsage || {});
      setDataLoaded(true);
    } catch (error) {
      console.error('❌ Error loading saved data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // On start, whether or not anyone is signed in.
  useEffect(() => {
    loadFromDevice();
  }, [loadFromDevice]);

  // Backup follows the hub session.
  useEffect(() => {
    hubSync.setSignedIn(!!user?.uid);
    setCloudSyncEnabled(hubSync.isBackupOn());
  }, [user?.uid]);

  // A restore from the account has rewritten saved data on this device:
  // reload it, including the points store's in-memory state (or its next save
  // would overwrite what was restored).
  useEffect(() => {
    const reload = () => {
      loadFromDevice();
      useGamificationStore.persist?.rehydrate?.();
    };
    window.addEventListener('rc:backup-restored', reload);
    return () => window.removeEventListener('rc:backup-restored', reload);
  }, [loadFromDevice]);

  // Wrapper function to save assessment results
  const setAssessmentResults = async (results) => {
    setAssessmentResultsState(results);
    if (results) {
      await saveAssessmentResults(null, results, DEVICE_ONLY);
      hubSync.noteLocalChange();
    }
  };

  // Module progress helper functions with auto-save
  const getModuleProgress = (moduleId) => {
    return moduleProgress?.[moduleId] || { completed: false, started: false };
  };

  const updateModuleProgress = async (moduleId, progressData) => {
    const updatedProgress = {
      ...moduleProgress,
      [moduleId]: {
        ...moduleProgress?.[moduleId],
        ...progressData,
        updatedAt: new Date().toISOString()
      }
    };

    setModuleProgressState(updatedProgress);
    await saveModuleProgress(null, moduleId, updatedProgress[moduleId], DEVICE_ONLY);
    hubSync.noteLocalChange();
  };

  const completeModule = async (moduleId) => {
    const completionData = {
      ...moduleProgress?.[moduleId],
      completed: true,
      completedAt: new Date().toISOString(),
      progress: 100
    };

    setModuleProgressState({ ...moduleProgress, [moduleId]: completionData });
    await saveModuleProgress(null, moduleId, completionData, DEVICE_ONLY);
    hubSync.noteLocalChange();
  };

  const isModuleCompleted = (moduleId) => {
    return moduleProgress?.[moduleId]?.completed || false;
  };

  // Tool data helpers with auto-save
  const updateToolData = async (toolId, data) => {
    const updatedData = {
      ...toolData,
      [toolId]: {
        ...toolData?.[toolId],
        ...data,
        updatedAt: new Date().toISOString()
      }
    };

    setToolDataState(updatedData);
    await saveToolData(null, toolId, updatedData[toolId], DEVICE_ONLY);
    hubSync.noteLocalChange();
  };

  const updateToolUsage = async (toolId, usage) => {
    const isFirstUse = !toolsUsage?.[toolId];

    const updatedUsage = {
      ...toolsUsage,
      [toolId]: {
        ...toolsUsage?.[toolId],
        ...usage,
        updatedAt: new Date().toISOString()
      }
    };

    setToolsUsageState(updatedUsage);

    // Award gamification points for tool usage
    const gamification = useGamificationStore.getState();
    if (isFirstUse) {
      gamification.completeTool(toolId); // First use awards more points
    } else {
      gamification.useToolSession(toolId); // Regular session points
    }

    await saveToolUsage(null, toolId, updatedUsage[toolId], DEVICE_ONLY);
    hubSync.noteLocalChange();
  };

  // Cloud backup (to the person's DDD account in the Auth Hub)
  const enableCloudSync = async () => {
    if (!user?.uid) {
      console.error('Cannot enable cloud backup: not signed in');
      return false;
    }
    // enableBackup switches the preference on before it waits for the
    // account, so the switch can move now rather than after the round trip.
    const pending = hubSync.enableBackup();
    setCloudSyncEnabled(hubSync.isBackupOn());
    try {
      const result = await pending;
      return result.ok;
    } catch (error) {
      console.warn('[AppContext] cloud backup could not reach the account:', error?.message || error);
      return false;
    } finally {
      setCloudSyncEnabled(hubSync.isBackupOn());
    }
  };

  const disableCloudSync = async () => {
    hubSync.disableBackup();
    setCloudSyncEnabled(false);
    return true;
  };

  // Empties this app's backup in the account and switches backup off here.
  const deleteCloudBackup = async () => {
    const result = await hubSync.deleteBackup();
    setCloudSyncEnabled(hubSync.isBackupOn());
    return result.ok;
  };

  const syncToCloud = async () => {
    if (!cloudSyncEnabled) return false;
    const result = await hubSync.flush();
    return result.pushed >= 0;
  };

  const syncFromCloud = async () => {
    if (!cloudSyncEnabled) return null;
    return hubSync.hydrate();
  };

  const value = {
    user,
    loading,
    dataLoaded,

    // Assessment
    assessmentResults,
    setAssessmentResults,

    // Module Progress
    moduleProgress,
    setModuleProgress: setModuleProgressState, // Direct setter (use updateModuleProgress instead)
    getModuleProgress,
    updateModuleProgress,
    completeModule,
    isModuleCompleted,

    // Tool Data & Usage
    toolData,
    toolsUsage,
    updateToolData,
    updateToolUsage,
    setToolsUsage: setToolsUsageState, // Direct setter (use updateToolUsage instead)

    // Cloud backup
    cloudSyncEnabled,
    enableCloudSync,
    disableCloudSync,
    deleteCloudBackup,
    syncToCloud,
    syncFromCloud,

    // UI Settings
    theme,
    setTheme,
    notifications,
    setNotifications
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
